/* eslint-disable react/react-in-jsx-scope */
import { useState, useEffect, useMemo, useCallback } from "react";
import { useIdleTimer } from "react-idle-timer";
import { BrowserRouter } from "react-router-dom";
import PageLayout from "./components/PageLayout";
import CssBaseline from "@mui/material/CssBaseline";
import { AppContext } from "./AppContext";
import { PowerBiReport, PowerBiDataset } from "./models/models";
import DashboardWebApi from "./services/DashboardWebApi";

const App = () => {
  const [tenantName, setTenantName] = useState<string>(null);
  const [reports, setReports] = useState<PowerBiReport[]>(null);
  const [datasets, setDatasets] = useState<PowerBiDataset[]>(null);
  const [userName, setUserName] = useState<string>(null);
  const [loginId, setLoginId] = useState<string>(null);
  const [userCanEdit, setUserCanEdit] = useState<boolean>(null);
  const [userCanCreate, setUserCanCreate] = useState<boolean>(null);
  const [workspaceArtifactsLoading, setWorkspaceArtifactsLoading] =
    useState<boolean>(null);
  let isAuthenticated = DashboardWebApi.IsAuthenticated();

  const onIdle = () => {
    isAuthenticated = false;
    DashboardWebApi.ResetLoginStatus();
    DashboardWebApi.ResetAuthentication();
    globalThis.location.replace(
      `${DashboardWebApi.SamlRoot}InitiateSingleSignOn?relayState=${globalThis.location.href}`,
    );
  };

  const onAction = () => {};

  useIdleTimer({
    onIdle,
    onAction,
    events: [
      "mousemove",
      "keydown",
      "wheel",
      "DOMMouseScroll",
      "mousewheel",
      "mousedown",
      "touchstart",
      "touchmove",
      "MSPointerDown",
      "MSPointerMove",
      "visibilitychange",
      "focus",
    ],
    timeout: 1000 * 60 * 60,
    throttle: 500,
  });

  const refreshEmbeddingData = useCallback(async () => {
    const viewModel = await DashboardWebApi.GetEmbeddingData();
    setReports(viewModel.reports);
  }, []); // No dependencies, function behavior never changes

  useEffect(() => {
    const getEmbeddingDataAsync = async () => {
      setWorkspaceArtifactsLoading(true);
      const viewModel = await DashboardWebApi.GetEmbeddingData();
      if (viewModel !== null) {
        setTenantName(viewModel.tenantName);
        setReports(viewModel.reports);
        setDatasets(viewModel.datasets);
        setUserCanEdit(viewModel.userCanEdit);
        setUserCanCreate(viewModel.userCanCreate);
      }

      const user = DashboardWebApi.GetUser();
      setLoginId(user.LoginId);
      setUserName(user.UserName);

      setWorkspaceArtifactsLoading(false);
    };
    if (isAuthenticated) {
      getEmbeddingDataAsync();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      setInterval(
        () => {
          const accessToken = DashboardWebApi.GetAccessToken();
          if (accessToken === "") {
            console.error(
              "Access token is invalid/missing, setting isAuthenticated to false",
            );
            isAuthenticated = false;
            DashboardWebApi.ResetLoginStatus();
            DashboardWebApi.ResetAuthentication();
            globalThis.location.replace(
              `${DashboardWebApi.SamlRoot}InitiateSingleSignOn?relayState=${globalThis.location.href}`,
            );
          } else {
            DashboardWebApi.GetRenewedJWT();
          }
        },
        5 * 60 * 1000,
      );
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      DashboardWebApi.ResetLoginStatus();
      const promise = DashboardWebApi.GetAuthorization();
      promise
        .then((accessToken) => {
          if (accessToken != null) {
            DashboardWebApi.LoginUser(accessToken);
          }
        })
        .catch((err) => console.error(err));
    }
  }, []);

  // Memoize context value to prevent unnecessary re-renders of all consumers
  const contextValue = useMemo(
    () => ({
      embeddingData: {
        tenantName: tenantName,
        reports: reports,
        datasets: datasets,
        user: userName,
        userCanEdit: userCanEdit,
        userCanCreate: userCanCreate,
        workspaceArtifactsLoading: workspaceArtifactsLoading,
        loginId: loginId,
      },
      refreshEmbeddingData: refreshEmbeddingData,
    }),
    [
      tenantName,
      reports,
      datasets,
      userName,
      userCanEdit,
      userCanCreate,
      workspaceArtifactsLoading,
      loginId,
      refreshEmbeddingData,
    ]
  );

  return (
    <AppContext.Provider value={contextValue}>
      <CssBaseline />
      <BrowserRouter>
        <PageLayout />
      </BrowserRouter>
    </AppContext.Provider>
  );
};

export default App;
